import React, { useState, useCallback } from "react";
import {
  Box, Container, Grid, Tabs, Tab, Typography, ToggleButtonGroup,
  ToggleButton, Alert, Skeleton, Pagination, Stack, Chip,
} from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import TableRowsIcon from "@mui/icons-material/TableRows";
import SchoolIcon from "@mui/icons-material/School";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { useAuth } from "../../../hooks/useAuth";
import EnrollmentFilters from "../components/EnrollmentFilters";
import EnrollmentCard from "../components/EnrollmentCard";
import EnrollmentTable from "../components/EnrollmentTable";
import { useEnrollmentDashboard } from "../../../hooks/useEnrollment";

const TABS = [
  { value: "all", label: "All Courses" },
  { value: "current", label: "Active" },
  { value: "completed", label: "Completed" },
  { value: "future", label: "Upcoming" },
];

const EMPTY_FILTERS = { search: "", status: "", quick: "" };

export default function CourseEnrollments() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState("all");
  const [viewMode, setViewMode] = useState("table");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(6);

  const handleFilter = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  }, []);

  const handleClear = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    setPage(1);
  }, []);

  const {
    enrollments,
    pagination,
    stats,
    isLoading,
    isError,
    error,
  } = useEnrollmentDashboard(user?.id, {
    moduleType: "course",
    searchTerm: filters.search,
    statusFilter: filters.status || null,
    activeTab,
    page,
    limit,
  });

  const handleAction = useCallback((action, enrollment) => {
    if (action === "view_details") navigate(`/enrollment/${enrollment.id}`);
  }, [navigate]);

  const totalPages = Math.ceil((pagination.total || 0) / limit) || 1;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f5f8fc", pb: 5 }}>
      <Box
        sx={{
          background: "linear-gradient(120deg, #061A38 0%, #0b3766 58%, #007d8a 100%)",
          color: "#fff",
          position: "relative",
          overflow: "hidden",
          "&::after": {
            content: '""',
            position: "absolute",
            width: 260,
            height: 260,
            border: "1px solid rgba(255,255,255,0.14)",
            borderRadius: "50%",
            right: -70,
            top: -120,
          },
        }}
      >
        <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 }, position: "relative", zIndex: 1 }}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={3}>
            <Box>
              <Chip
                icon={<SchoolIcon sx={{ color: "#8ff5ef !important" }} />}
                label="LEARNING HUB"
                size="small"
                sx={{ bgcolor: "rgba(255,255,255,0.12)", color: "#bafbf7", fontWeight: 800, letterSpacing: 1, mb: 1.5 }}
              />
              <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: "-0.02em", fontSize: { xs: "1.8rem", md: "2.35rem" } }}>
                Your learning journey
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.72)", mt: 0.75, maxWidth: 480 }}>
                Pick up where you left off, explore your enrolled courses, and keep your progress moving forward.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap>
              <Box sx={{ minWidth: 128, p: 1.5, borderRadius: 2.5, bgcolor: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.12)" }}>
                <AutoStoriesIcon sx={{ color: "#8ff5ef", fontSize: 20 }} />
                <Typography sx={{ fontSize: 24, fontWeight: 800, lineHeight: 1.2 }}>{stats?.total ?? 0}</Typography>
                <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.68)" }}>Enrolled</Typography>
              </Box>
              <Box sx={{ minWidth: 128, p: 1.5, borderRadius: 2.5, bgcolor: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.12)" }}>
                <TaskAltIcon sx={{ color: "#b8f3c8", fontSize: 20 }} />
                <Typography sx={{ fontSize: 24, fontWeight: 800, lineHeight: 1.2 }}>{stats?.completed ?? 0}</Typography>
                <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.68)" }}>Completed</Typography>
              </Box>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ mt: { xs: -1, md: -2 }, position: "relative" }}>

        {isError && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error?.message ?? "Failed to load courses."}</Alert>
        )}

        <Box sx={{ bgcolor: "#fff", borderRadius: 3, p: { xs: 1.5, md: 2 }, boxShadow: "0 8px 30px rgba(6,26,56,0.07)" }}>
          <EnrollmentFilters filters={filters} onFilterChange={handleFilter} onClear={handleClear} />

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.25, mt: 2, flexWrap: "wrap", gap: 1 }}>
            <Tabs
              value={activeTab}
              onChange={(_, v) => { setActiveTab(v); setPage(1); }}
              sx={{ minHeight: 38, "& .MuiTabs-indicator": { height: 3, borderRadius: 3 }, "& .MuiTab-root": { minHeight: 38, py: 0.5, fontSize: "0.82rem", fontWeight: 700 } }}
            >
              {TABS.map((tab) => <Tab key={tab.value} value={tab.value} label={tab.label} />)}
            </Tabs>

            <ToggleButtonGroup value={viewMode} exclusive onChange={(_, v) => v && setViewMode(v)} size="small" sx={{ bgcolor: "#f3f6fa", p: 0.35, borderRadius: 2.5, "& .MuiToggleButton-root": { border: 0, borderRadius: 2, px: 1.1, color: "#64748b" }, "& .Mui-selected": { bgcolor: "#fff !important", color: "#0057ff !important", boxShadow: "0 2px 8px rgba(15,23,42,0.1)" } }}>
              <ToggleButton value="card" aria-label="Card view"><ViewModuleIcon fontSize="small" /></ToggleButton>
              <ToggleButton value="table" aria-label="Table view"><TableRowsIcon fontSize="small" /></ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {!isLoading && pagination.total > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, px: 0.5, flexWrap: "wrap", gap: 1 }}>
              <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                Showing <strong>{(page - 1) * limit + 1}–{Math.min(page * limit, pagination.total)}</strong> of <strong>{pagination.total}</strong>
              </Typography>
              <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} color="primary" size="small" shape="rounded" />
            </Box>
          )}

          <AnimatePresence mode="wait">
            {viewMode === "card" ? (
              <motion.div key="cards" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.18 }}>
                {isLoading ? (
                  <Grid container spacing={2}>
                    {Array.from({ length: 6 }).map((_, i) => (
                      <Grid key={i} size={{ xs: 12, sm: 6, lg: 4 }}>
                        <Skeleton variant="rectangular" height={290} sx={{ borderRadius: 3 }} />
                      </Grid>
                    ))}
                  </Grid>
                ) : enrollments.length === 0 ? (
                  <Box sx={{ py: 9, textAlign: "center" }}>
                    <Typography variant="h6" color="text.secondary">No courses found</Typography>
                    <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>Try a different filter</Typography>
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {enrollments.map((enrollment, i) => (
                      <Grid key={enrollment.id ?? i} size={{ xs: 12, sm: 6, lg: 4 }}>
                        <EnrollmentCard enrollment={enrollment} onAction={handleAction} index={i} />
                      </Grid>
                    ))}
                  </Grid>
                )}
              </motion.div>
            ) : (
              <motion.div key="table" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.18 }}>
                <EnrollmentTable
                  enrollments={enrollments}
                  pagination={pagination}
                  loading={isLoading}
                  onPageChange={setPage}
                  onLimitChange={(l) => { setLimit(l); setPage(1); }}
                  onAction={handleAction}
                  hidePagination
                />
              </motion.div>
            )}
          </AnimatePresence>
        </Box>
      </Container>
    </Box>
  );
}
